import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import clsx from "clsx";
import {
  AlignLeft,
  CalendarClock,
  MessageSquare,
  ScrollText,
  StickyNote,
} from "lucide-react";
import { FC, useEffect, useMemo } from "react";
import { useGetProject } from "~/api/projects/projects";
import {
  AdaptiveTabs,
  AdaptiveTabsContent,
  AdaptiveTabsList,
} from "~/components/ui/adaptive-tabs";
import { ChronologyArea } from "~/features/chronology/components/ChronologyArea";
import { NotesArea } from "~/features/notes/components/NotesArea";
import { useSkyViewStore } from "~/features/skyView/store";
import { useChatDocumentParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { useDocumentSearchParams } from "~/hooks/useDocumentSearchParams";
import { ChatDocumentTab, useTabOrder } from "~/hooks/useTabOrder";
import { isChronologyEnabled } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { useDocList } from "../../hooks/useDocList";
import { useSelectedEntityStore } from "../../stores/selectedEntityStore";
import { ChatArea } from "../ChatArea";
import { ReportArea } from "../reportArea";
import { SummaryArea } from "../SummaryArea";
import { ExpandableControl } from "./ExpandableControl";
import { SortableTabTrigger } from "./SortableTabTrigger";

const TABS = [
  {
    id: "report",
    label: "chat.report.title",
    icon: ScrollText,
  },
  {
    id: "notes",
    label: "common.notes",
    icon: StickyNote,
  },
  {
    id: "chronology",
    label: "common.chronology",
    icon: CalendarClock,
  },
  {
    id: "chat",
    label: "common.chat",
    icon: MessageSquare,
  },
  {
    id: "summary",
    label: "common.summary",
    icon: AlignLeft,
  },
];

export const DocSidebar: FC<{ isGuest: boolean }> = ({ isGuest }) => {
  const [{ tab, pageId }, updateParam] = useDocumentSearchParams();
  const isExpanded = useSkyViewStore((state) => state.isExpanded);
  const { tabOrder, saveTabOrder } = useTabOrder();
  const { docList } = useDocList();
  const { setSelectedEntity } = useSelectedEntityStore();
  const { isMobile } = useMobile();
  const { organizationId, caseId, isViewOnly } = useChatDocumentParams();
  const { data: project } = useGetProject(organizationId, caseId);
  const hasChronologyEvents: boolean | undefined =
    project != null &&
    "hasChronologyEvents" in project &&
    typeof project.hasChronologyEvents === "boolean"
      ? project.hasChronologyEvents
      : undefined;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const reorderedTabs = useMemo(() => {
    return tabOrder
      .map((tabId) => TABS.find((tab) => tab.id === tabId)!)
      .filter(Boolean)
      .filter((tab) => {
        if (!isChronologyEnabled() && tab.id === "chronology") {
          return false;
        }
        if (isGuest && (tab.id === "notes" || tab.id === "chronology")) {
          return false;
        }
        if (isViewOnly && tab.id === "chronology") {
          return false;
        }
        if (tab.id === "chronology" && hasChronologyEvents === false) {
          return false;
        }
        return true;
      });
  }, [tabOrder, isGuest, isViewOnly, hasChronologyEvents]);

  useEffect(() => {
    if (reorderedTabs.length === 0) {
      return;
    }
    const isActiveTabVisible = reorderedTabs.some((item) => item.id === tab);
    if (!isActiveTabVisible) {
      updateParam({
        tab: reorderedTabs[0].id as ChatDocumentTab,
      });
    }
  }, [tab, reorderedTabs, updateParam]);

  const handleTabChange = (value: string) => {
    const eventName = `app/doc_sidebar/${value}_tab.trigger/click`;
    trackEvent({ eventName });

    // When clicking on summary tab, add parameters from URL to store
    if (value === "summary") {
      if (pageId) {
        setSelectedEntity("page", pageId, []);
      } else if (docList) {
        // Find section by sectionId or by pageId
        const selectedSection = docList.tree[0].sections[0];
        if (selectedSection) {
          setSelectedEntity(
            "section",
            selectedSection.id,
            selectedSection.pageIds || []
          );
        }
      }
    }

    updateParam({
      tab: value as ChatDocumentTab,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = tabOrder.indexOf(active.id as ChatDocumentTab);
    const newIndex = tabOrder.indexOf(over.id as ChatDocumentTab);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = [...tabOrder];
      const [removed] = newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, removed);

      saveTabOrder(newOrder);

      trackEvent({
        eventName: "app/doc_sidebar/tab_reorder/drag",
        properties: {
          from: oldIndex,
          to: newIndex,
          tabId: active.id,
        },
      });
    }
  };

  return (
    <div
      className={clsx("flex h-full", {
        "col-span-3": isExpanded,
      })}
    >
      <ExpandableControl />
      {(isExpanded || isMobile) && (
        <div className="border-border w-full min-w-0">
          <AdaptiveTabs value={tab} onValueChange={handleTabChange}>
            <div className="h-[60px]">
              <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={tabOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  <AdaptiveTabsList data-tour="ai-sidebar" className="h-full">
                    {reorderedTabs.map((tab) => (
                      <SortableTabTrigger
                        key={tab.id}
                        id={tab.id}
                        value={tab.id}
                        label={tab.label}
                        icon={tab.icon}
                      />
                    ))}
                  </AdaptiveTabsList>
                </SortableContext>
              </DndContext>
            </div>
            {reorderedTabs.map((tabItem) => (
              <AdaptiveTabsContent key={tabItem.id} value={tabItem.id}>
                {tabItem.id === "report" && <ReportArea />}
                {tabItem.id === "notes" && (
                  <NotesArea isActiveTab={tab === "notes"} />
                )}
                {tabItem.id === "chronology" && <ChronologyArea />}
                {tabItem.id === "chat" && <ChatArea />}
                {tabItem.id === "summary" && <SummaryArea />}
              </AdaptiveTabsContent>
            ))}
          </AdaptiveTabs>
        </div>
      )}
    </div>
  );
};
